<script setup lang="ts">
// ===== 1. IMPORTS =====
import { useField } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import {
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch
} from 'vue'
import { useFocus } from '@vueuse/core'
import EmailHelper from '@/exports/emailHelper'
import intlTelInput, { type Iti } from 'intl-tel-input'
import 'intl-tel-input/styles'
import { vMaska } from 'maska/vue'

// ===== 2. TYPE DEFINITIONS =====
interface Props {
  type?: string
  name: string
  required?: boolean
  label: string
  successMessage?: string
  errorMessage?: string
  placeholder?: string
  focus?: boolean
  maxLength?: number
  ariaDescribedby?: string
  hint?: string
  pattern?: string
  inputClass?: string
  regExForValidation?: RegExp
  mask?: string
  passwordVisibilityOption?: boolean
  labelClass?: string
  disabled?: boolean
  hintClass?: string
}

interface Emits {
  errors: [error: string]
  fullNumber: [val: string]
}

// ===== 3. PROPS =====
const {
  type = 'text',
  name,
  required = false,
  label,
  successMessage = '',
  errorMessage = undefined,
  placeholder = undefined,
  focus = false,
  maxLength = 200,
  ariaDescribedby = undefined,
  hint = undefined,
  pattern = undefined,
  inputClass = undefined,
  regExForValidation = undefined,
  mask = undefined,
  passwordVisibilityOption = false,
  labelClass = undefined,
  disabled = false,
  hintClass = undefined
} = defineProps<Props>()

const modelValue = defineModel<string>('modelValue')

// ===== 4. EMITS =====
const emit = defineEmits<Emits>()

// ===== 5. COMPOSABLES & EXTERNAL DEPENDENCIES =====
const { t } = useI18n()

const input = useTemplateRef('input')
const inputContainer = useTemplateRef('input-container')

const { focused } = useFocus(input, { initialValue: focus })

const {
  value,
  setErrors,
  errors,
  errorMessage: fieldErrorMessage,
  meta,
  handleBlur,
  handleChange,
  validate
} = useField(name, validateFormField, {
  initialValue: modelValue.value,
  checkedValue: modelValue.value,
  syncVModel: true,
  validateOnValueUpdate: false
})

// ===== 6. REACTIVE DATA =====
const iti = shallowRef<Iti | null>(null)
const isUtilsReady = shallowRef(false)
const formFieldType = ref(type)
const telephoneValidationUnavailableMessage =
  'Phone number validation is temporarily unavailable.'
const invalidTelephoneNumberMessage = 'Please provide a valid phone number.'
let pendingTelephoneValue: string | null = null

const validationListeners = {
  blur: onBlur,
  change: handleChange,
  input: (evt: Event | undefined) =>
    handleChange(evt, !!fieldErrorMessage.value)
}

// ===== 7. COMPUTED PROPERTIES =====

// ===== 8. WATCHERS =====
watch(
  () => focus,
  (value) => {
    focused.value = value
  }
)

watch(value, (newValue) => {
  if (type === 'tel') {
    handleTelephoneValueChange(newValue ?? '')
    return
  }

  if (required) {
    if (!newValue) {
      emit('errors', 'This field is required.')
    }
  }
})

// ===== 9. LIFECYCLE HOOKS =====
onMounted(() => {
  if (type !== 'tel' || !input.value) return

  const instance = intlTelInput(input.value, {
    onlyCountries: ['us', 'ca', 'au', 'gb', 'nz'],
    countrySearch: false,
    loadUtils: () => import('intl-tel-input/utils'),
    separateDialCode: true,
    initialCountry: 'us',
    numberDisplayFormat: 'NATIONAL',
    countrySelectorMode: 'DROPDOWN'
  })

  iti.value = instance

  instance.promise
    .then(async () => {
      if (iti.value !== instance) return

      isUtilsReady.value = true
      if (pendingTelephoneValue === null) return

      const pendingValue = pendingTelephoneValue
      pendingTelephoneValue = null
      await validate()
      if (iti.value !== instance) return

      handleTelephoneValueChange(pendingValue)
    })
    .catch((error: unknown) => {
      console.error(
        '[TextInput] Failed to load telephone validation utilities:',
        error
      )
      if (iti.value !== instance) return

      isUtilsReady.value = false
      const currentValue = value.value ?? ''
      if (!required && currentValue.length === 0) {
        pendingTelephoneValue = null
        setErrors([])
        return
      }

      pendingTelephoneValue = currentValue
      setTelephoneError(telephoneValidationUnavailableMessage)
    })
})

onBeforeUnmount(() => {
  iti.value?.destroy()
  iti.value = null
  isUtilsReady.value = false
  pendingTelephoneValue = null
})

// ===== 10. METHODS =====
function onBlur(evt: Event | undefined): void {
  if (
    type === 'tel' &&
    !disabled &&
    value.value &&
    isUtilsReady.value &&
    iti.value?.isValidNumberPrecise()
  ) {
    // Reading the national format avoids setNumber's country inference/events.
    value.value = iti.value.getNumber('NATIONAL')
  }

  handleBlur(evt, true)
}

function handleTelephoneValueChange(value: string): void {
  if (value.length === 0) {
    pendingTelephoneValue = null

    if (required) {
      setTelephoneError(
        isUtilsReady.value
          ? errorMessage || 'This field is required.'
          : telephoneValidationUnavailableMessage
      )
      return
    }

    setErrors([])
    emit('fullNumber', '')
    return
  }

  if (!iti.value || !isUtilsReady.value) {
    pendingTelephoneValue = value
    setTelephoneError(telephoneValidationUnavailableMessage)
    return
  }

  if (!iti.value.isValidNumberPrecise()) {
    setTelErrorMessage(value)
    return
  }

  const fullNumber = iti.value.getNumber()
  if (!fullNumber) {
    setTelephoneError(invalidTelephoneNumberMessage)
    return
  }

  setErrors([])
  emit('fullNumber', fullNumber)
}

function validateFormField(value: string): string | boolean {
  if (required) {
    if (!value?.trim()) {
      return errorMessage ? errorMessage : 'This field is required.'
    }
  }
  if (type === 'email') {
    if (value?.match(EmailHelper.getEmailRegex()) === null) {
      return 'Please provide a valid email.'
    }
  }
  if (type === 'tel') {
    if (!required && (!value || value.length === 0)) {
      pendingTelephoneValue = null
      return true
    }
    if (!iti.value || !isUtilsReady.value) {
      pendingTelephoneValue = value
      return telephoneValidationUnavailableMessage
    }
    if (!iti.value.isValidNumberPrecise()) {
      return getTelErrorMessage(value ?? '')
    }
    if (!iti.value.getNumber()) {
      return invalidTelephoneNumberMessage
    }
  }
  if (regExForValidation) {
    if (!value || value?.match(regExForValidation) === null) {
      return 'This value is invalid.'
    }
  }
  if (type === 'zip') {
    if (
      value === null ||
      value?.match(/^ *$/) !== null ||
      value?.match(/(^\d{5}$)|(^\d{5}-\d{4}$)/) === null
    ) {
      return 'Please provide a valid zip code.'
    }
  }
  return true
}

function setTelErrorMessage(value: string): void {
  if (iti.value == null) return
  const errorMessageText = getTelErrorMessage(value)

  setTelephoneError(errorMessageText)
}

function getTelErrorMessage(value: string): string {
  let errorMessageText = invalidTelephoneNumberMessage

  if (value.length)
    errorMessageText =
      'The number is shorter than all valid numbers for this region.'

  switch (iti.value?.getValidationError()) {
    case intlTelInput.VALIDATION_ERROR.IS_POSSIBLE:
      errorMessageText = t('validation.phoneInvalid')
      break
    case intlTelInput.VALIDATION_ERROR.INVALID_COUNTRY_CODE:
      errorMessageText = 'The number has an invalid country calling code.'
      break
    case intlTelInput.VALIDATION_ERROR.TOO_SHORT:
      errorMessageText =
        'The number is shorter than all valid numbers for this region.'
      break
    case intlTelInput.VALIDATION_ERROR.TOO_LONG:
      errorMessageText =
        'The number is longer than all valid numbers for this region.'
      break
    case intlTelInput.VALIDATION_ERROR.IS_POSSIBLE_LOCAL_ONLY:
      errorMessageText =
        'The number length matches that of local numbers for this region only.'
      break
    case intlTelInput.VALIDATION_ERROR.INVALID_LENGTH:
      errorMessageText =
        'The number does not match the length for valid numbers for this region.'
      break
  }

  return errorMessageText
}

function setTelephoneError(error: string): void {
  setErrors(error)
  emit('errors', error)
}

function toggleFieldType(): void {
  formFieldType.value = formFieldType.value === 'password' ? 'text' : 'password'
}

function scrollToComponent(): void {
  inputContainer.value?.scrollIntoView()
}

defineExpose({ scrollToComponent })
</script>

<template>
  <div
    ref="input-container"
    class="form-group w-100 flex-column"
    :class="{
      'has-error': !!fieldErrorMessage,
      success: meta.valid,
      disabled: disabled
    }"
  >
    <label :for="name" class="font-size-1 font-medium" :class="labelClass">
      {{ label }}

      <small
        v-if="hint"
        class="label-hint d-block font-weight-2"
        :class="hintClass"
      >
        <strong>{{ hint }}</strong>
      </small>
    </label>

    <div class="flex align-justify input-container">
      <input
        :id="name"
        ref="input"
        v-model="value"
        v-maska
        class="font-size-1"
        :class="inputClass"
        :name="name"
        :type="formFieldType"
        :maxLength="maxLength"
        :aria-describedby="ariaDescribedby"
        :placeholder="placeholder"
        :pattern="pattern"
        :data-maska="mask"
        :disabled="disabled"
        v-on="validationListeners"
      />

      <button
        v-show="
          type === 'password' &&
          formFieldType === 'password' &&
          passwordVisibilityOption
        "
        type="button"
        class="btn tertiary show-btn brand"
        style="
          margin-left: -10rem;
          margin-right: 1rem;
          margin-inline: -10rem 1rem;
        "
        @click="toggleFieldType"
      >
        Show
      </button>

      <button
        v-show="
          type === 'password' &&
          formFieldType !== 'password' &&
          passwordVisibilityOption
        "
        type="button"
        class="btn tertiary show-btn brand"
        style="
          margin-left: -10rem;
          margin-right: 1rem;
          margin-inline: -10rem 1rem;
        "
        @click="toggleFieldType"
      >
        Hide
      </button>
    </div>

    <span v-if="errors.length > 0" class="error-message">{{ errors[0] }}</span>

    <span v-if="successMessage && meta.valid" class="success-message">
      {{ successMessage }}
    </span>
  </div>
</template>

<style scoped>
.font-medium {
  font-variation-settings: 'wght' 550;
}

.show-btn {
  font-size: var(--font-size-00) !important;
  font-weight: var(--font-weigth-1) !important;
}

.disabled {
  opacity: 0.5;
}

.label-hint {
  color: var(--text-2);
  margin-top: 0.5rem;
  font-size: var(--font-size-0);
  &:empty {
    display: none;
  }
}

input {
  height: 3.125rem;

  background: oklch(from var(--gray-12) l c h / calc(alpha - 0.97));

  border-radius: 12px;
}

input:-moz-placeholder {
  color: var(--gray-5);
  opacity: 1;
}

input::-moz-placeholder {
  color: var(--gray-5);
  opacity: 1;
}

input:-ms-input-placeholder {
  color: var(--gray-5);
  opacity: 1;
}

input::-webkit-input-placeholder {
  color: var(--gray-5);
  opacity: 1;
}

input::placeholder {
  color: var(--gray-5);
  opacity: 1;
}

.has-error div input {
  outline: 2px solid var(--danger);

  background: oklch(from var(--danger) l c h / calc(alpha - 0.97));
}

:deep(.iti) {
  z-index: 0;
}

:deep(.iti__selected-country) {
  background: transparent;
  border-right: 1px solid var(--gray-4);
  border-top-left-radius: var(--radius-3);
  border-bottom-left-radius: var(--radius-3);

  padding-inline-end: var(--size-00);

  &:hover {
    background: var(--surface-2);
  }
}

:deep(.iti__selected-country-primary) {
  border-top-left-radius: var(--radius-3);
  border-bottom-left-radius: var(--radius-3);
  --iti-hover-color: transparent;
}

:deep(.iti__selected-dial-code) {
  color: var(--text-2);
}

:deep(.iti__arrow) {
  border-block-color: var(--text-2);
}

:deep(.iti__country-name) {
  color: var(--text-0);
}

:deep(.iti__country) {
  --iti-hover-color: var(--surface-2);
}

:deep(.iti__country-selector) {
  --iti-country-selector-bg: var(--surface-1);
}

:deep(.iti__country-list) {
  background: var(--surface-1);
}

strong {
  --base-weight: 400;
}

.input-container > :deep(.iti) {
  z-index: 1;
}
</style>
